import { AnalyticsService } from '@alison/analytics/client';
import { Button } from '@alison/ui';
import { useTenant } from '@alison/whitelabel/react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Snackbar from '@mui/material/Snackbar';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { type ReactNode, useState } from 'react';
import { TenantLogo } from '@/components/TenantLogo.js';
import { useTRPC } from '@/lib/trpc.js';

interface InviteAcceptSearch {
  token?: string;
}

function CenteredScreen({ children }: { children: ReactNode }): ReactNode {
  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        p: 2,
      }}
    >
      {children}
    </Box>
  );
}

function InviteAcceptPage(): ReactNode {
  const { providerName } = useTenant();
  const navigate = useNavigate();
  const { token } = Route.useSearch();
  const trpc = useTRPC();

  const inviteQuery = useQuery({
    ...trpc.auth.getInvite.queryOptions({ token: token ?? '' }),
    enabled: token !== undefined,
    retry: false,
  });
  const acceptMutation = useMutation(trpc.auth.acceptInvite.mutationOptions());
  const [accepted, setAccepted] = useState(false);

  async function onAccept(): Promise<void> {
    if (token === undefined) return;
    await acceptMutation.mutateAsync({ token });
    if (inviteQuery.data?.isAlisonStaff !== true) {
      AnalyticsService.track('Invite - Accepted');
    }
    setAccepted(true);
  }

  if (token === undefined) {
    return (
      <CenteredScreen>
        <Alert severity="error">This invite link is missing its token.</Alert>
      </CenteredScreen>
    );
  }

  if (inviteQuery.isPending) {
    return (
      <CenteredScreen>
        <CircularProgress />
      </CenteredScreen>
    );
  }

  if (inviteQuery.isError || inviteQuery.data === undefined) {
    return (
      <CenteredScreen>
        <Alert severity="error">This invite is invalid or has expired.</Alert>
      </CenteredScreen>
    );
  }

  const invite = inviteQuery.data;

  return (
    <CenteredScreen>
      <Box
        sx={{
          width: '100%',
          maxWidth: 440,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Box sx={{ mb: 3 }}>
          <TenantLogo />
        </Box>

        <Paper
          elevation={0}
          sx={{
            width: '100%',
            p: 5,
            borderRadius: 2,
            border: 1,
            borderColor: 'divider',
            boxShadow: 5,
          }}
        >
          <Stack spacing={4}>
            <Box>
              <Typography
                variant="h2"
                align="center"
                sx={(theme) => ({ ...theme.mixins.gradientText, mb: 2 })}
              >
                You're invited
              </Typography>
              <Typography variant="body1" align="center" color="text.secondary" sx={{ mt: 1 }}>
                You've been invited to join {providerName}.
              </Typography>
            </Box>

            {invite.accepted ? (
              <Alert severity="info">This invite has already been accepted.</Alert>
            ) : (
              <Button
                fullWidth
                variant="gradient"
                onClick={() => void onAccept()}
                disabled={acceptMutation.isPending || accepted}
                data-testid="button-accept-invite"
                sx={{ py: 2 }}
              >
                Accept invite
              </Button>
            )}

            <Typography variant="caption" color="text.secondary" align="center">
              You'll sign in with SSO after accepting.
            </Typography>
          </Stack>
        </Paper>
      </Box>

      <Snackbar
        open={accepted}
        autoHideDuration={2500}
        onClose={() => void navigate({ to: '/login' })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="success"
          sx={{ borderRadius: 1, fontSize: 13, fontWeight: 600, alignItems: 'center' }}
        >
          Invite accepted - taking you to sign in…
        </Alert>
      </Snackbar>
    </CenteredScreen>
  );
}

export const Route = createFileRoute('/invites/accept/')({
  component: InviteAcceptPage,
  validateSearch: (search: Record<string, unknown>): InviteAcceptSearch =>
    typeof search['token'] === 'string' ? { token: search['token'] } : {},
});
